import React, { useState } from 'react';
import { toast } from 'react-hot-toast';
import { UserPlusIcon, BuildingOfficeIcon } from '@heroicons/react/24/outline';
import { apiClient, adminAPI } from '../services/api';
import EntityDrawer from './ui/EntityDrawer';
import InvoiceEditorForm, { InvoiceEditorLineItem } from './InvoiceEditorForm';
import CustomerTypeahead, { CustomerTypeaheadOption } from './customer/CustomerTypeahead';
import AccountTypeahead, { AccountTypeaheadOption } from './account/AccountTypeahead';
import ReservationTypeahead, { ReservationTypeaheadOption } from './reservations/ReservationTypeahead';

interface CreateInvoiceModalProps {
  onClose: () => void;
  onSuccess: (invoice?: any) => void;
  initialCustomer?: CustomerTypeaheadOption | null;
  initialAccount?: AccountTypeaheadOption | null;
  initialReservation?: ReservationTypeaheadOption | null;
  subtitle?: string;
}

export default function CreateInvoiceModal({
  onClose,
  onSuccess,
  initialCustomer = null,
  initialAccount = null,
  initialReservation = null,
  subtitle = 'Build a customer invoice with shared pricing, terms, and memo controls.',
}: CreateInvoiceModalProps) {
  const [selectedCustomer, setSelectedCustomer] = useState<CustomerTypeaheadOption | null>(initialCustomer);
  const [selectedAccount, setSelectedAccount] = useState<AccountTypeaheadOption | null>(initialAccount);
  const [selectedReservation, setSelectedReservation] = useState<ReservationTypeaheadOption | null>(initialReservation);

  // Inline "create new" states
  const [showNewCustomer, setShowNewCustomer] = useState(false);
  const [newCustomerFirstName, setNewCustomerFirstName] = useState('');
  const [newCustomerLastName, setNewCustomerLastName] = useState('');
  const [newCustomerEmail, setNewCustomerEmail] = useState('');
  const [newCustomerPhone, setNewCustomerPhone] = useState('');
  const [newCustomerCompany, setNewCustomerCompany] = useState('');
  const [newCustomerAccountMode, setNewCustomerAccountMode] = useState<'none' | 'link_existing' | 'create_new'>('none');
  const [newCustomerAccount, setNewCustomerAccount] = useState<AccountTypeaheadOption | null>(null);
  const [newCustomerAccountName, setNewCustomerAccountName] = useState('');
  const [newCustomerPrimaryBilling, setNewCustomerPrimaryBilling] = useState(false);
  const [creatingCustomer, setCreatingCustomer] = useState(false);

  const [showNewAccount, setShowNewAccount] = useState(false);
  const [newAccountName, setNewAccountName] = useState('');
  const [creatingAccount, setCreatingAccount] = useState(false);
  const [paymentTerms, setPaymentTerms] = useState('due_on_receipt');
  const [dueDate, setDueDate] = useState('');
  const [taxPercentage, setTaxPercentage] = useState(0);
  const [discountAmount, setDiscountAmount] = useState(0);
  const [memo, setMemo] = useState('');
  const [description, setDescription] = useState('');
  const [lineItems, setLineItems] = useState<InvoiceEditorLineItem[]>([
    { id: '1', description: '', quantity: 1, unit_amount: 0 },
  ]);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const addLineItem = () => {
    setLineItems([
      ...lineItems,
      { id: Date.now().toString(), description: '', quantity: 1, unit_amount: 0 },
    ]);
  };

  const removeLineItem = (id: string) => {
    if (lineItems.length > 1) {
      setLineItems(lineItems.filter((item) => item.id !== id));
    }
  };

  const updateLineItem = (id: string, field: keyof InvoiceEditorLineItem, value: string | number) => {
    setLineItems(lineItems.map((item) => (item.id === id ? { ...item, [field]: value } : item)));
  };

  const subtotal = lineItems.reduce((sum, item) => sum + item.quantity * item.unit_amount, 0);
  const subtotalAfterDiscount = Math.max(0, subtotal - discountAmount);
  const taxAmount = subtotalAfterDiscount * (taxPercentage / 100);
  const total = subtotalAfterDiscount + taxAmount;

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();

    const customerId = selectedCustomer?.id || '';

    if (!customerId) {
      toast.error('Please select a customer');
      return;
    }

    const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
    const stripeCustomerRegex = /^cus_[A-Za-z0-9]+$/;
    if (!uuidRegex.test(customerId) && !stripeCustomerRegex.test(customerId)) {
      toast.error('Invalid customer selected. Please refresh and try again.');
      return;
    }

    if (lineItems.some((item) => !item.description || item.unit_amount < 0)) {
      toast.error('All line items must have a description and a non-negative amount');
      return;
    }

    setIsSubmitting(true);

    try {
      const response = await apiClient.post('/invoices/admin/custom', {
        customer_id: customerId,
        account_id: selectedAccount?.id || undefined,
        reservation_id: selectedReservation?.id || undefined,
        line_items: lineItems.map((item) => ({
          description: item.description,
          quantity: item.quantity,
          unit_amount: Math.round(item.unit_amount * 100),
        })),
        tax_rate: taxPercentage / 100,
        discount_amount: Math.round(discountAmount * 100),
        payment_terms: paymentTerms,
        due_date: dueDate || undefined,
        memo,
        description,
      });

      toast.success('Invoice created successfully');
      onSuccess(response.data?.invoice || response.data);
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to create invoice');
    } finally {
      setIsSubmitting(false);
    }
  };

  const formatCurrency = (amount: number) => new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
  }).format(amount);

  async function handleCreateCustomer() {
    if (!newCustomerEmail.trim() || !newCustomerFirstName.trim()) {
      toast.error('First name and email are required');
      return;
    }
    setCreatingCustomer(true);
    try {
      if (newCustomerAccountMode === 'link_existing' && !newCustomerAccount?.id) {
        toast.error('Select an account for this customer');
        return;
      }
      if (newCustomerAccountMode === 'create_new' && !(newCustomerAccountName.trim() || newCustomerCompany.trim())) {
        toast.error('Enter an account name');
        return;
      }
      const res = await adminAPI.createCustomer({
        email: newCustomerEmail.trim(),
        firstName: newCustomerFirstName.trim(),
        lastName: newCustomerLastName.trim(),
        phone: newCustomerPhone.trim() || undefined,
        companyName: newCustomerCompany.trim() || undefined,
        accountRelationship: {
          mode: newCustomerAccountMode,
          accountId: newCustomerAccountMode === 'link_existing' ? newCustomerAccount?.id || null : null,
          accountName: newCustomerAccountMode === 'create_new'
            ? newCustomerAccountName.trim() || newCustomerCompany.trim()
            : null,
          makePrimaryBillingContact: newCustomerPrimaryBilling,
        },
      });
      const created = res.data?.customer || res.data;
      if (created?.id) {
        const fullName = [newCustomerFirstName.trim(), newCustomerLastName.trim()].filter(Boolean).join(' ');
        setSelectedCustomer({
          id: String(created.id),
          label: fullName || newCustomerEmail.trim(),
          description: newCustomerEmail.trim(),
          email: newCustomerEmail.trim(),
        });
        toast.success('Customer created');
        setShowNewCustomer(false);
        setNewCustomerFirstName('');
        setNewCustomerLastName('');
        setNewCustomerEmail('');
        setNewCustomerPhone('');
        setNewCustomerCompany('');
        setNewCustomerAccountMode('none');
        setNewCustomerAccount(null);
        setNewCustomerAccountName('');
        setNewCustomerPrimaryBilling(false);
        if (created.account?.id) {
          setSelectedAccount({
            id: String(created.account.id),
            label: created.account.name || 'Account',
            description: 'Linked from customer creation',
          });
        }
      }
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to create customer');
    } finally {
      setCreatingCustomer(false);
    }
  }

  async function handleCreateAccount() {
    if (!newAccountName.trim()) {
      toast.error('Account name is required');
      return;
    }
    setCreatingAccount(true);
    try {
      const res = await adminAPI.createAccount({ name: newAccountName.trim() });
      const created = res.data;
      if (created?.id) {
        setSelectedAccount({
          id: String(created.id),
          label: created.name || newAccountName.trim(),
          description: undefined,
        });
        toast.success('Account created');
        setShowNewAccount(false);
        setNewAccountName('');
      }
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to create account');
    } finally {
      setCreatingAccount(false);
    }
  }

  return (
    <EntityDrawer
      open={true}
      title="Create Invoice"
      subtitle={subtitle}
      onClose={onClose}
      widthClassName="max-w-4xl"
    >
      <InvoiceEditorForm
        customerFields={(
          <div className="space-y-4">
            {/* ── Customer ── */}
            <div>
              <div className="mb-1 flex items-center justify-between">
                <label className="text-sm font-medium text-on-surface-variant">Customer *</label>
                <button
                  type="button"
                  onClick={() => { setShowNewCustomer((v) => !v); setSelectedCustomer(null); }}
                  className="inline-flex items-center gap-1 text-xs text-primary hover:underline"
                >
                  <UserPlusIcon className="h-3.5 w-3.5" />
                  {showNewCustomer ? 'Search existing' : '+ New customer'}
                </button>
              </div>

              {showNewCustomer ? (
                <div className="rounded-card border border-ghost bg-surface-container-low p-3 space-y-3">
                  <p className="text-xs font-medium text-on-surface-variant">Create new customer</p>
                  <div className="grid grid-cols-2 gap-2">
                    <input
                      type="text"
                      placeholder="First name *"
                      value={newCustomerFirstName}
                      onChange={(e) => setNewCustomerFirstName(e.target.value)}
                      className="input-field text-sm"
                    />
                    <input
                      type="text"
                      placeholder="Last name"
                      value={newCustomerLastName}
                      onChange={(e) => setNewCustomerLastName(e.target.value)}
                      className="input-field text-sm"
                    />
                  </div>
                  <input
                    type="email"
                    placeholder="Email *"
                    value={newCustomerEmail}
                    onChange={(e) => setNewCustomerEmail(e.target.value)}
                    className="input-field w-full text-sm"
                  />
                  <div className="grid grid-cols-2 gap-2">
                    <input
                      type="tel"
                      placeholder="Phone"
                      value={newCustomerPhone}
                      onChange={(e) => setNewCustomerPhone(e.target.value)}
                      className="input-field text-sm"
                    />
                    <input
                      type="text"
                      placeholder="Business / company label"
                      value={newCustomerCompany}
                      onChange={(e) => setNewCustomerCompany(e.target.value)}
                      className="input-field text-sm"
                    />
                  </div>
                  <div className="rounded-lg border border-ghost bg-surface-container-lowest p-3">
                    <p className="mb-2 text-xs font-medium uppercase tracking-wide text-on-surface-variant">Account relationship</p>
                    <div className="grid grid-cols-3 gap-2 text-xs">
                      {[
                        { value: 'none', label: 'None' },
                        { value: 'link_existing', label: 'Link' },
                        { value: 'create_new', label: 'Create' },
                      ].map((option) => (
                        <label
                          key={option.value}
                          className={`rounded-md border px-2 py-2 ${
                            newCustomerAccountMode === option.value
                              ? 'border-primary bg-primary/10 text-on-surface'
                              : 'border-outline-variant text-on-surface-variant'
                          }`}
                        >
                          <input
                            type="radio"
                            className="mr-1"
                            checked={newCustomerAccountMode === option.value}
                            onChange={() => setNewCustomerAccountMode(option.value as typeof newCustomerAccountMode)}
                          />
                          {option.label}
                        </label>
                      ))}
                    </div>
                    {newCustomerAccountMode === 'link_existing' ? (
                      <div className="mt-2">
                        <AccountTypeahead
                          value={newCustomerAccount}
                          onChange={setNewCustomerAccount}
                          placeholder="Search accounts..."
                        />
                      </div>
                    ) : null}
                    {newCustomerAccountMode === 'create_new' ? (
                      <input
                        type="text"
                        placeholder={newCustomerCompany || 'Account name'}
                        value={newCustomerAccountName}
                        onChange={(e) => setNewCustomerAccountName(e.target.value)}
                        className="input-field mt-2 text-sm"
                      />
                    ) : null}
                    {newCustomerAccountMode !== 'none' ? (
                      <label className="mt-2 flex items-center gap-2 text-xs text-on-surface-variant">
                        <input
                          type="checkbox"
                          checked={newCustomerPrimaryBilling}
                          onChange={(e) => setNewCustomerPrimaryBilling(e.target.checked)}
                        />
                        Primary billing contact
                      </label>
                    ) : null}
                  </div>
                  <div className="flex gap-2">
                    <button
                      type="button"
                      onClick={handleCreateCustomer}
                      disabled={creatingCustomer}
                      className="btn-primary text-sm"
                    >
                      {creatingCustomer ? 'Creating...' : 'Create & select'}
                    </button>
                    <button
                      type="button"
                      onClick={() => setShowNewCustomer(false)}
                      className="btn-secondary text-sm"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              ) : (
                <>
                  <CustomerTypeahead
                    value={selectedCustomer}
                    onChange={setSelectedCustomer}
                    placeholder="Search by name or email..."
                  />
                  <p className="mt-1 text-xs text-on-surface-variant">Start typing to search customers by name or email.</p>
                </>
              )}
            </div>

            {/* ── Account ── */}
            <div>
              <div className="mb-1 flex items-center justify-between">
                <label className="text-sm font-medium text-on-surface-variant">Account (optional)</label>
                <button
                  type="button"
                  onClick={() => { setShowNewAccount((v) => !v); setSelectedAccount(null); }}
                  className="inline-flex items-center gap-1 text-xs text-primary hover:underline"
                >
                  <BuildingOfficeIcon className="h-3.5 w-3.5" />
                  {showNewAccount ? 'Search existing' : '+ New account'}
                </button>
              </div>

              {showNewAccount ? (
                <div className="rounded-card border border-ghost bg-surface-container-low p-3 space-y-3">
                  <p className="text-xs font-medium text-on-surface-variant">Create new account</p>
                  <input
                    type="text"
                    placeholder="Account name *"
                    value={newAccountName}
                    onChange={(e) => setNewAccountName(e.target.value)}
                    className="input-field w-full text-sm"
                  />
                  <div className="flex gap-2">
                    <button
                      type="button"
                      onClick={handleCreateAccount}
                      disabled={creatingAccount}
                      className="btn-primary text-sm"
                    >
                      {creatingAccount ? 'Creating...' : 'Create & select'}
                    </button>
                    <button
                      type="button"
                      onClick={() => setShowNewAccount(false)}
                      className="btn-secondary text-sm"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              ) : (
                <>
                  <AccountTypeahead
                    value={selectedAccount}
                    onChange={setSelectedAccount}
                    placeholder="Search account by name..."
                  />
                  <p className="mt-1 text-xs text-on-surface-variant">Link this invoice to a billing account. A reservation may share invoices across multiple accounts.</p>
                </>
              )}
            </div>

            {/* ── Reservation ── */}
            <div>
              <label className="mb-1 block text-sm font-medium text-on-surface-variant">
                Reservation (optional)
              </label>
              <ReservationTypeahead
                value={selectedReservation}
                onChange={setSelectedReservation}
                placeholder="Search reservations by ref or customer..."
              />
              <p className="mt-1 text-xs text-on-surface-variant">
                One reservation can have multiple invoices attached — this links for reference only.
              </p>
            </div>
          </div>
        )}
        description={description}
        setDescription={setDescription}
        lineItems={lineItems}
        addLineItem={addLineItem}
        removeLineItem={removeLineItem}
        updateLineItem={updateLineItem}
        discountAmount={discountAmount}
        setDiscountAmount={setDiscountAmount}
        taxPercentage={taxPercentage}
        setTaxPercentage={setTaxPercentage}
        paymentTerms={paymentTerms}
        setPaymentTerms={setPaymentTerms}
        dueDate={dueDate}
        setDueDate={setDueDate}
        memo={memo}
        setMemo={setMemo}
        subtotal={subtotal}
        taxAmount={taxAmount}
        total={total}
        formatCurrency={formatCurrency}
        onSubmit={handleSubmit}
        onCancel={onClose}
        submitLabel="Create Invoice"
        isSubmitting={isSubmitting}
      />
    </EntityDrawer>
  );
}
